import { call, put, select } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import { change } from 'redux-form'

import { parseErrors, getOpenApiInstance, Response } from '../../api/api'

import { productActions, modalLoaderActions, analyticsActions } from '../../redux'

import { getLoanConfigurator, getLoanPreConfigurator } from './product.mappers'
import * as codetableMappers from '../codetable/codetable.mappers'

import { ErrSuccess, LoanConfigInitResponse, LoanConfigResponse, Saga } from '../../types'
import { LoanRange } from '../../redux/reducers/codetable/model'
import { Insurance } from '../../constants'

export function* saveLoanPreConfigurator({
  payload: { success, error },
}: PayloadAction<ErrSuccess>): Saga {
  const loader = yield put(modalLoaderActions.showModalLoader())

  const requestData = yield select(getLoanPreConfigurator)

  yield put(productActions.setDataPreConfigurator(requestData))

  const response = yield call(getOpenApiInstance().saveLoanPreConfigurator, requestData)

  if (response.responseStatus === Response.ok) {
    success()
  } else if (response.responseStatus === Response.dataError) {
    error({ type: response.responseStatus, errors: parseErrors(response) })
  } else {
    error({ type: response.responseStatus })
  }

  yield put(modalLoaderActions.hideModalLoader(loader.id))
}

export function* initLoanConfigurator({
  payload: { success, error, data },
}: PayloadAction<ErrSuccess>): Saga {
  const loader = yield put(modalLoaderActions.showModalLoader())
  const response = yield call(getOpenApiInstance().initLoanConfigurator)

  if (response.responseStatus === Response.ok) {
    // @ts-ignore
    const respObject: LoanConfigInitResponse = checkAndRepairBounds(response.data)

    yield put(productActions.setInitialData(respObject))
    yield put(change('LoanConfigurator', 'loanAmount', respObject.configuration.loanAmount))
    yield put(change('LoanConfigurator', 'term', respObject.configuration.term))
    yield put(
      change(
        'LoanConfigurator',
        'insuranceRequested',
        respObject.insuranceRequested === Insurance.Yes
      )
    )

    if (!data.isRescoring) {
      yield put(analyticsActions.setScoringOk(respObject))
      // send data to the analytics
      yield put(analyticsActions.setAnalyticsPage(undefined))
    }

    success()
  } else {
    error(response.responseStatus)
  }

  yield put(modalLoaderActions.hideModalLoader(loader.id))
}

export function* updateLoanConfigurator({
  payload: { success, error },
}: PayloadAction<ErrSuccess>): Saga {
  const loanData = yield select(getLoanConfigurator)
  const loanRanges = yield select(codetableMappers.getLoanRanges)
  const clientData = adjustLoanData(loanData, getAllowedTerms(loanRanges))
  yield put(productActions.setUpdatedData(clientData))
  yield put(change('LoanConfigurator', 'loanAmount', clientData.loanAmount))
  yield put(change('LoanConfigurator', 'term', clientData.term))

  const response = yield call(getOpenApiInstance().updateLoanConfigurator, clientData)

  if (response.responseStatus === Response.ok) {
    const respObject: LoanConfigResponse = response.data
    checkAndRepairBounds(respObject)

    const responseData: LoanConfigResponse = respObject
    delete responseData.configuration.loanAmount
    delete responseData.configuration.term

    yield put(productActions.setUpdatedData(responseData))
    success()
  } else {
    error(response.responseStatus)
  }
}

export function* commitLoanConfiguratorRe({
  payload: { success, error },
}: PayloadAction<any>): Saga {
  const loader = yield put(modalLoaderActions.showModalLoader())
  const response = yield call(getOpenApiInstance().commitLoanConfiguratorRescoring)

  if (response.responseStatus === Response.ok) {
    success()
  } else {
    error(response.responseStatus)
  }
  yield put(modalLoaderActions.hideModalLoader(loader.id))
}

const checkAndRepairBounds = (respObject: LoanConfigResponse): LoanConfigResponse => {
  let term = respObject.configuration.term
  let loanAmount = respObject.configuration.loanAmount
  if (respObject.configuration.term > respObject.configuration.termTo) {
    term = respObject.configuration.termTo
  } else if (respObject.configuration.term < respObject.configuration.termFrom) {
    term = respObject.configuration.termFrom
  }

  if (respObject.configuration.loanAmount > respObject.configuration.amountTo) {
    loanAmount = respObject.configuration.amountTo
  } else if (respObject.configuration.loanAmount < respObject.configuration.amountFrom) {
    loanAmount = respObject.configuration.amountFrom
  }

  return { ...respObject, configuration: { ...respObject.configuration, term, loanAmount } }
}

const getAllowedTerms = (loanRanges: LoanRange[]): number[] => {
  const terms: number[] = []
  loanRanges.forEach(lr => {
    terms.push(...lr.terms)
  })
  return terms
}

const adjustLoanData = (loanData: any, allowedTerms: number[]) => {
  const roundedAmount = Math.round(loanData.loanAmount / 1000) * 1000
  let nearestTerm: number = allowedTerms[0]
  for (let i = 1; i < allowedTerms.length; i++) {
    if (Math.abs(loanData.term - allowedTerms[i]) < Math.abs(loanData.term - nearestTerm)) {
      nearestTerm = allowedTerms[i]
    }
  }
  return { ...loanData, loanAmount: roundedAmount, term: nearestTerm }
}
